import React, { createContext, useContext, useMemo } from 'react';

import { useHttpClientIntegration } from '@/shared/infra/http/HttpClientContext';
import { ApiDuplicateCaseRepository } from '../repositories/ApiDuplicateCaseRepository';
import { FetchDuplicateCaseLookupsUseCase } from '../../application/use-cases/FetchDuplicateCaseLookupsUseCase';
import { ContinueCreateDuplicateCaseUseCase } from '../../application/use-cases/ContinueCreateDuplicateCaseUseCase';
import { AbandonDuplicateCaseUseCase } from '../../application/use-cases/AbandonDuplicateCaseUseCase';
import { LinkExistingCaseUseCase } from '../../application/use-cases/LinkExistingCaseUseCase';
import { ApiPendingEverifyRepository } from '@/features/pending_everify/infra/repositories/ApiPendingEverifyRepository';
import { FetchPendingEverifyItemsUseCase } from '@/features/pending_everify/application/use-cases/FetchPendingEverifyItemsUseCase';

interface DuplicateCaseDependencies {
  fetchDuplicateCaseLookupsUseCase: FetchDuplicateCaseLookupsUseCase;
  continueCreateDuplicateCaseUseCase: ContinueCreateDuplicateCaseUseCase;
  abandonDuplicateCaseUseCase: AbandonDuplicateCaseUseCase;
  linkExistingCaseUseCase: LinkExistingCaseUseCase;
  fetchPendingEverifyItemsUseCase: FetchPendingEverifyItemsUseCase;
}

const DuplicateCaseDIContext = createContext<DuplicateCaseDependencies | undefined>(undefined);

export const DuplicateCaseDIProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const httpClient = useHttpClientIntegration();

    const dependencies = useMemo(() => {
    const duplicateCaseRepository = new ApiDuplicateCaseRepository(httpClient);
    const fetchDuplicateCaseLookupsUseCase = new FetchDuplicateCaseLookupsUseCase(
      duplicateCaseRepository
    );
    const continueCreateDuplicateCaseUseCase = new ContinueCreateDuplicateCaseUseCase(
      duplicateCaseRepository
    );
    const abandonDuplicateCaseUseCase = new AbandonDuplicateCaseUseCase(duplicateCaseRepository);
    const linkExistingCaseUseCase = new LinkExistingCaseUseCase(duplicateCaseRepository);
    const pendingEverifyRepository = new ApiPendingEverifyRepository(httpClient);
    const fetchPendingEverifyItemsUseCase = new FetchPendingEverifyItemsUseCase(
      pendingEverifyRepository
    );

    return {
      fetchDuplicateCaseLookupsUseCase,
      continueCreateDuplicateCaseUseCase,
      abandonDuplicateCaseUseCase,
      linkExistingCaseUseCase,
      fetchPendingEverifyItemsUseCase,
    };
  }, [httpClient]);

  return (
    <DuplicateCaseDIContext.Provider value={dependencies}>
      {children}
    </DuplicateCaseDIContext.Provider>
  );
};

export const useDuplicateCaseDI = (): DuplicateCaseDependencies => {
  const context = useContext(DuplicateCaseDIContext);
  if (!context) {
    throw new Error('useDuplicateCaseDI must be used within a DuplicateCaseDIProvider');
  }
  return context;
};
